import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiForgotPassword } from '../utils/backendApi';
import { Lock, Mail, User, AlertTriangle, Shield, Clock, Phone, Eye, EyeOff, Zap, CheckCircle } from 'lucide-react';
import Logo from '../components/ui/Logo';

// Lazy-load an external script tag once per page; resolves when it's ready
// (or the global it sets is already present). Both Google's GSI client and
// Apple's auth SDK are huge enough that we don't want them in the app bundle
// nor loaded on every page — only on /login.
function loadExternalScript(src, globalCheck) {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined') return reject(new Error('No window'));
    if (globalCheck && globalCheck()) return resolve();
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      existing.addEventListener('load',  () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('script error')), { once: true });
      return;
    }
    const s = document.createElement('script');
    s.src = src; s.async = true; s.defer = true;
    s.addEventListener('load',  () => resolve(), { once: true });
    s.addEventListener('error', () => reject(new Error('script error')), { once: true });
    document.head.appendChild(s);
  });
}

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';
const APPLE_CLIENT_ID  = import.meta.env.VITE_APPLE_CLIENT_ID  || '';

export default function LoginPage() {
  const { login, signup, loading, loginWithGoogle, loginWithApple } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState('login'); // 'login' | 'signup' | 'pending' | 'forgot' | 'forgot-sent'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [pendingMsg, setPendingMsg] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(null); // 'google' | 'apple' | null
  const [googleReady, setGoogleReady] = useState(false);
  const [appleReady,  setAppleReady]  = useState(false);

  // Load Google + Apple SDKs lazily — only on this page, only once per
  // session. Each provider script sets a global (`window.google` /
  // `window.AppleID`) once it parses; we surface a "Ready" flag so the
  // buttons stay disabled until the SDK is actually usable.
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;
    loadExternalScript('https://accounts.google.com/gsi/client', () => !!window.google?.accounts?.id)
      .then(() => setGoogleReady(true))
      .catch(() => setGoogleReady(false));
  }, []);
  useEffect(() => {
    if (!APPLE_CLIENT_ID) return;
    loadExternalScript(
      'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js',
      () => !!window.AppleID
    )
      .then(() => setAppleReady(true))
      .catch(() => setAppleReady(false));
  }, []);

  const switchMode = (m) => {
    setMode(m);
    setError('');
    setPassword('');
    setConfirmPassword('');
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  const handleForgotPassword = async () => {
    setError('');
    if (!email || !/\S+@\S+\.\S+/.test(email)) return setError('Please enter a valid email address');
    setForgotLoading(true);
    try {
      await apiForgotPassword(email);
      setMode('forgot-sent');
    } catch (err) {
      setError(err.message || 'Failed to send reset email');
    }
    setForgotLoading(false);
  };

  // ── OAuth handlers ─────────────────────────────────────────────────────
  // Both providers share the same backend contract: receive id_token,
  // verify, look up or create user by email, return our JWT or a
  // PENDING_APPROVAL code. Errors flow through the standard `error` state.
  const handleGoogleSignIn = () => {
    if (!GOOGLE_CLIENT_ID) {
      setError('Google sign-in is not configured. Set VITE_GOOGLE_CLIENT_ID on the client and GOOGLE_CLIENT_ID on the server.');
      return;
    }
    if (!googleReady || !window.google?.accounts?.id) {
      setError('Google sign-in is still loading — try again in a moment.');
      return;
    }
    setError('');
    setOauthLoading('google');
    try {
      // We initialise each time rather than once at script-load so a stale
      // callback closure can never re-fire on the next click.
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        ux_mode:   'popup',
        callback:  async (response) => {
          if (!response?.credential) {
            setOauthLoading(null);
            setError('Google did not return a credential. Try again.');
            return;
          }
          try {
            await loginWithGoogle(response.credential);
            navigate('/dashboard', { replace: true });
          } catch (err) {
            if (err.code === 'PENDING_APPROVAL') {
              setPendingMsg(err.message);
              setMode('pending');
            } else {
              setError(err.message || 'Google sign-in failed');
            }
          } finally {
            setOauthLoading(null);
          }
        },
      });
      window.google.accounts.id.prompt(); // shows the One Tap / popup chooser
    } catch (e) {
      setOauthLoading(null);
      setError(e.message || 'Could not start Google sign-in');
    }
  };

  const handleAppleSignIn = async () => {
    if (!APPLE_CLIENT_ID) {
      setError('Apple sign-in is not configured. Set VITE_APPLE_CLIENT_ID on the client and APPLE_CLIENT_ID on the server.');
      return;
    }
    if (!appleReady || !window.AppleID) {
      setError('Apple sign-in is still loading — try again in a moment.');
      return;
    }
    setError('');
    setOauthLoading('apple');
    try {
      window.AppleID.auth.init({
        clientId:    APPLE_CLIENT_ID,
        scope:       'name email',
        redirectURI: window.location.origin + '/login',
        state:       'signin-' + Date.now(),
        usePopup:    true,
      });
      const data = await window.AppleID.auth.signIn();
      const idToken = data?.authorization?.id_token;
      if (!idToken) throw new Error('Apple did not return an id_token');
      try {
        await loginWithApple(idToken, data.user || null);
        navigate('/dashboard', { replace: true });
      } catch (err) {
        if (err.code === 'PENDING_APPROVAL') {
          setPendingMsg(err.message);
          setMode('pending');
        } else {
          setError(err.message || 'Apple sign-in failed');
        }
      }
    } catch (e) {
      // popup_closed_by_user is a clean cancel — don't surface as an error.
      if (e?.error && e.error !== 'popup_closed_by_user') {
        setError(e.error || 'Apple sign-in failed');
      } else if (!e?.error && e?.message) {
        setError(e.message);
      }
    } finally {
      setOauthLoading(null);
    }
  };

  const handleLogin = async () => {
    setError('');
    if (!email || !password) return setError('Please fill in all fields');
    try {
      await login(email, password);
      // Always land on the dashboard, regardless of watchlist contents —
      // dashboard is the canonical "you just logged in" surface (track-record
      // card, market headline, portfolio tile). Users who want their
      // watchlist can click into it.
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err.message || 'Login failed');
    }
  };

  const handleSignup = async () => {
    setError('');
    if (!name || !email || !password) return setError('Please fill in all fields');
    if (name.length < 2) return setError('Name must be at least 2 characters');
    if (!/\S+@\S+\.\S+/.test(email)) return setError('Invalid email address');
    if (password.length < 8) return setError('Password must be at least 8 characters');
    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password)) {
      return setError('Password needs uppercase, lowercase, and a number');
    }
    if (password !== confirmPassword) {
      return setError('Passwords do not match');
    }
    try {
      const result = await signup(name, email, password, { phone });
      setPendingMsg(result.message || 'Account created! Please wait for admin approval. Once approved, your 30-day trial begins.');
      setMode('pending');
    } catch (err) {
      setError(err.message || 'Signup failed');
    }
  };

  const inputStyle = {
    width: '100%', padding: '12px 14px 12px 42px', borderRadius: 10,
    border: '1px solid rgba(51,65,85,0.6)', background: 'rgba(15,23,42,0.6)',
    color: '#e2e8f0', fontSize: 14, fontFamily: "'DM Sans', sans-serif",
    outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s',
  };

  const eyeBtnStyle = {
    position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
    background: 'none', border: 'none', color: '#475569', cursor: 'pointer',
    display: 'flex', alignItems: 'center', padding: 2,
  };

  const oauthBtnStyle = {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
    width: '100%', padding: '12px 14px', borderRadius: 10,
    border: '1px solid rgba(51,65,85,0.6)', background: '#0f172a',
    color: '#e2e8f0', fontSize: 14, fontWeight: 600, cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
    transition: 'background var(--dur-fast, 180ms) ease, border-color var(--dur-fast, 180ms) ease',
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(135deg, #0a0e17 0%, #111827 40%, #0f172a 100%)',
      position: 'relative', overflow: 'hidden',
    }}>
      {/* Background effects */}
      <div style={{ position: 'absolute', inset: 0, opacity: 0.04,
        backgroundImage: 'linear-gradient(rgba(99,102,241,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(99,102,241,0.3) 1px, transparent 1px)',
        backgroundSize: '60px 60px' }} />
      <div className="animate-pulse-glow" style={{
        position: 'absolute', top: '10%', left: '20%', width: 400, height: 400, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(99,102,241,0.15) 0%, transparent 70%)', filter: 'blur(60px)' }} />
      <div className="animate-pulse-glow" style={{
        position: 'absolute', bottom: '10%', right: '15%', width: 300, height: 300, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(16,185,129,0.12) 0%, transparent 70%)', filter: 'blur(50px)', animationDelay: '1.5s' }} />

      <div className="auth-card animate-slide-up" style={{
        width: '100%', maxWidth: 440, background: 'rgba(15,23,42,0.8)', backdropFilter: 'blur(20px)',
        borderRadius: 20, border: '1px solid rgba(99,102,241,0.15)', padding: '48px 40px',
        position: 'relative', zIndex: 1, margin: '0 16px',
        boxShadow: '0 25px 60px rgba(0,0,0,0.5), 0 0 40px rgba(99,102,241,0.05)',
      }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <div style={{ marginBottom: 8 }}>
            <Logo size={30} />
          </div>
          <p style={{ color: 'var(--text-muted)', fontSize: 14, margin: 0 }}>Smart Signals for NYSE & NASDAQ</p>
        </div>

        {/* Forgot password — email entry */}
        {mode === 'forgot' && (
          <div>
            <h3 style={{ color: '#f1f5f9', fontSize: 18, margin: '0 0 6px 0' }}>Reset Password</h3>
            <p style={{ color: '#94a3b8', fontSize: 13, margin: '0 0 20px 0', lineHeight: 1.5 }}>
              Enter your account email and we'll send you a reset link.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ position: 'relative' }}>
                <Mail size={16} style={{ position: 'absolute', left: 14, top: 14, color: '#475569' }} />
                <input
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="Email Address"
                  type="email"
                  style={inputStyle}
                  autoComplete="email"
                  name="email"
                  inputMode="email"
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck={false}
                  onKeyDown={e => e.key === 'Enter' && handleForgotPassword()}
                />
              </div>
              {error && (
                <div style={{ padding: '10px 14px', borderRadius: 8, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', color: '#f87171', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <AlertTriangle size={14} />{error}
                </div>
              )}
              <button
                onClick={handleForgotPassword}
                disabled={forgotLoading}
                style={{ width: '100%', padding: '13px 0', borderRadius: 10, border: 'none', background: forgotLoading ? '#475569' : 'linear-gradient(135deg, #6366f1, #7c3aed)', color: '#fff', fontSize: 15, fontWeight: 600, cursor: forgotLoading ? 'wait' : 'pointer', fontFamily: 'inherit' }}>
                {forgotLoading ? 'Sending...' : 'Send Reset Link'}
              </button>
              <button onClick={() => switchMode('login')} style={{ background: 'none', border: 'none', color: '#64748b', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'center' }}>
                Back to Sign In
              </button>
            </div>
          </div>
        )}

        {/* Forgot password — sent confirmation */}
        {mode === 'forgot-sent' && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ width: 64, height: 64, borderRadius: '50%', margin: '0 auto 16px', background: 'rgba(16,185,129,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CheckCircle size={32} color="#10b981" />
            </div>
            <h3 style={{ color: '#f1f5f9', fontSize: 18, margin: '0 0 8px 0' }}>Check your email</h3>
            <p style={{ color: '#94a3b8', fontSize: 13, lineHeight: 1.5, margin: '0 0 20px 0' }}>
              If an account with that email exists, a password reset link has been sent. The link expires in 1 hour.
            </p>
            <button onClick={() => switchMode('login')} style={{ padding: '10px 24px', borderRadius: 8, border: '1px solid rgba(99,102,241,0.3)', background: 'transparent', color: '#818cf8', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
              Back to Sign In
            </button>
          </div>
        )}

        {/* Pending approval state */}
        {mode === 'pending' && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{
              width: 64, height: 64, borderRadius: '50%', margin: '0 auto 16px',
              background: 'rgba(99,102,241,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}><Clock size={32} color="#818cf8" /></div>
            <h3 style={{ color: '#f1f5f9', fontSize: 18, margin: '0 0 8px 0' }}>Awaiting Approval</h3>
            <p style={{ color: '#94a3b8', fontSize: 13, lineHeight: 1.5, margin: '0 0 20px 0' }}>
              {pendingMsg}
            </p>
            <button onClick={() => switchMode('login')}
              style={{
                padding: '10px 24px', borderRadius: 8, border: '1px solid rgba(99,102,241,0.3)',
                background: 'transparent', color: '#818cf8', fontSize: 13, fontWeight: 600,
                cursor: 'pointer', fontFamily: 'inherit',
              }}>
              Back to Sign In
            </button>
          </div>
        )}

        {/* Login / Signup forms */}
        {mode !== 'pending' && mode !== 'forgot' && mode !== 'forgot-sent' && (
          <>
            {/* Toggle tabs */}
            <div style={{
              display: 'flex', borderRadius: 12, background: 'rgba(30,41,59,0.6)',
              padding: 4, marginBottom: 28, border: '1px solid rgba(51,65,85,0.5)',
            }}>
              {[{ key: 'login', label: 'Sign In' }, { key: 'signup', label: 'Sign Up' }].map(tab => (
                <button key={tab.key} onClick={() => switchMode(tab.key)}
                  style={{
                    flex: 1, padding: '10px 0', borderRadius: 9, border: 'none', cursor: 'pointer',
                    fontSize: 14, fontWeight: 600, fontFamily: 'inherit', transition: 'all 0.2s',
                    background: mode === tab.key ? 'linear-gradient(135deg, #6366f1, #7c3aed)' : 'transparent',
                    color: mode === tab.key ? '#fff' : '#64748b',
                    boxShadow: mode === tab.key ? '0 2px 10px rgba(99,102,241,0.3)' : 'none',
                  }}>{tab.label}</button>
              ))}
            </div>

            {/* OAuth buttons — Google + Apple. Loaded only on /login via
                lazy <script> tags. Each button stays disabled until its
                SDK is parsed; when the env var for a provider isn't set,
                the button is omitted entirely. */}
            {(GOOGLE_CLIENT_ID || APPLE_CLIENT_ID) && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
                {GOOGLE_CLIENT_ID && (
                  <button
                    type="button"
                    onClick={handleGoogleSignIn}
                    disabled={oauthLoading != null || loading}
                    style={oauthBtnStyle}
                    onMouseEnter={(e) => { if (oauthLoading == null && !loading) e.currentTarget.style.background = '#1f2937'; }}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#0f172a'}
                  >
                    <GoogleLogoSvg />
                    {oauthLoading === 'google' ? 'Signing in with Google…' : 'Continue with Google'}
                  </button>
                )}
                {APPLE_CLIENT_ID && (
                  <button
                    type="button"
                    onClick={handleAppleSignIn}
                    disabled={oauthLoading != null || loading}
                    style={oauthBtnStyle}
                    onMouseEnter={(e) => { if (oauthLoading == null && !loading) e.currentTarget.style.background = '#1f2937'; }}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#0f172a'}
                  >
                    <AppleLogoSvg />
                    {oauthLoading === 'apple' ? 'Signing in with Apple…' : 'Continue with Apple'}
                  </button>
                )}
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  margin: '6px 0', fontSize: 11, color: '#475569',
                }}>
                  <span style={{ flex: 1, height: 1, background: 'rgba(51,65,85,0.4)' }} />
                  <span style={{ letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                    or {mode === 'login' ? 'sign in' : 'sign up'} with email
                  </span>
                  <span style={{ flex: 1, height: 1, background: 'rgba(51,65,85,0.4)' }} />
                </div>
              </div>
            )}

            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (mode === 'login') handleLogin();
                else handleSignup();
              }}
              style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
            >
              {/* Name field (signup only) */}
              {mode === 'signup' && (
                <div style={{ position: 'relative' }}>
                  <User size={16} style={{ position: 'absolute', left: 14, top: 14, color: '#475569' }} />
                  <input value={name} onChange={e => setName(e.target.value)} placeholder="Full Name" style={inputStyle} autoComplete="name" name="name" />
                </div>
              )}

              {/* Phone (signup only) */}
              {mode === 'signup' && (
                <div style={{ position: 'relative' }}>
                  <Phone size={16} style={{ position: 'absolute', left: 14, top: 14, color: '#475569' }} />
                  <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="Phone (optional)" style={inputStyle} type="tel" autoComplete="tel" name="tel" />
                </div>
              )}

              {/* Email */}
              <div style={{ position: 'relative' }}>
                <Mail size={16} style={{ position: 'absolute', left: 14, top: 14, color: '#475569' }} />
                <input
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="Email Address"
                  type="email"
                  style={inputStyle}
                  autoComplete="email"
                  name="email"
                  inputMode="email"
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck={false}
                />
              </div>

              {/* Password */}
              <div style={{ position: 'relative' }}>
                <Lock size={16} style={{ position: 'absolute', left: 14, top: 14, color: '#475569' }} />
                <input
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Password"
                  type={showPassword ? 'text' : 'password'}
                  style={{ ...inputStyle, paddingRight: 40 }}
                  autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                  name="password"
                  spellCheck={false}
                  onKeyDown={e => e.key === 'Enter' && mode === 'login' && handleLogin()}
                />
                <button type="button" style={eyeBtnStyle} onClick={() => setShowPassword(v => !v)} tabIndex={-1}>
                  {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>

              {/* Forgot password link (login only) */}
              {mode === 'login' && (
                <div style={{ textAlign: 'right', marginTop: -8 }}>
                  <button onClick={() => switchMode('forgot')} style={{ background: 'none', border: 'none', color: '#6366f1', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}>
                    Forgot password?
                  </button>
                </div>
              )}

              {/* Confirm Password (signup only) */}
              {mode === 'signup' && (
                <div style={{ position: 'relative' }}>
                  <Lock size={16} style={{ position: 'absolute', left: 14, top: 14, color: '#475569' }} />
                  <input
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    placeholder="Confirm Password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    style={{
                      ...inputStyle, paddingRight: 40,
                      borderColor: confirmPassword && confirmPassword !== password
                        ? 'rgba(239,68,68,0.5)' : 'rgba(51,65,85,0.6)',
                    }}
                    autoComplete="new-password"
                    name="confirm-password"
                    spellCheck={false}
                    onKeyDown={e => e.key === 'Enter' && handleSignup()}
                  />
                  <button type="button" style={eyeBtnStyle} onClick={() => setShowConfirmPassword(v => !v)} tabIndex={-1}>
                    {showConfirmPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              )}

              {/* Password requirements (signup only) */}
              {mode === 'signup' && (
                <div style={{ fontSize: 11, color: '#475569', lineHeight: 1.6 }}>
                  Password must have: 8+ characters, uppercase, lowercase, number
                </div>
              )}

              {/* Error */}
              {error && (
                <div style={{
                  padding: '10px 14px', borderRadius: 8, background: 'rgba(239,68,68,0.1)',
                  border: '1px solid rgba(239,68,68,0.2)', color: '#f87171', fontSize: 13,
                  display: 'flex', alignItems: 'center', gap: 6,
                }}>
                  <AlertTriangle size={14} />{error}
                </div>
              )}

              {/* Submit button — type="submit" so the form's onSubmit fires,
                  which is what iOS Safari needs to classify the form for
                  the credential manager (autofill + password save). The
                  onSubmit handler does e.preventDefault() so the page
                  doesn't reload. */}
              <button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%', padding: '13px 0', borderRadius: 10, border: 'none',
                  background: loading ? '#475569' : 'linear-gradient(135deg, #6366f1, #7c3aed)',
                  color: '#fff', fontSize: 15, fontWeight: 600, cursor: loading ? 'wait' : 'pointer',
                  fontFamily: 'inherit', boxShadow: '0 4px 15px rgba(99,102,241,0.3)',
                }}>
                {loading ? 'Processing...' : mode === 'login' ? 'Sign In' : 'Create Account'}
              </button>
            </form>

            {mode === 'signup' && (
              <p style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, color: '#475569', fontSize: 12, marginTop: 16 }}>
                <Zap size={11} style={{ flexShrink: 0 }} />
                Sign up today and get a 30-day free trial upon admin approval.
              </p>
            )}
          </>
        )}

        <div style={{
          marginTop: 28, paddingTop: 20, borderTop: '1px solid rgba(51,65,85,0.3)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          color: '#475569', fontSize: 12,
        }}>
          <Shield size={12} style={{ flexShrink: 0 }} />
          End-to-end encrypted · Secure authentication
        </div>
      </div>

      <style>{`
        @media (max-width: 480px) {
          .auth-card { padding: 32px 20px !important; border-radius: 16px !important; margin: 0 12px !important; }
        }
        @media (max-width: 360px) {
          .auth-card { padding: 24px 16px !important; }
        }
      `}</style>
    </div>
  );
}

// Brand-correct inline SVG logos. Inline keeps them in the bundle (no extra
// network request) and lets them inherit the button's flexbox layout without
// any extra wrapper.
function GoogleLogoSvg() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8a12 12 0 1 1 0-24c3 0 5.8 1.1 7.9 3l5.7-5.7A20 20 0 0 0 24 4a20 20 0 1 0 19.6 16.5z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8A12 12 0 0 1 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7A20 20 0 0 0 24 4 20 20 0 0 0 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 44a20 20 0 0 0 13.5-5.2l-6.2-5.3A12 12 0 0 1 12.7 28.4l-6.5 5A20 20 0 0 0 24 44z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3a12 12 0 0 1-4.1 5.5l6.2 5.3A20 20 0 0 0 44 24c0-1.2-.1-2.4-.4-3.5z"/>
    </svg>
  );
}

function AppleLogoSvg() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M17.05 12.04c-.03-3.02 2.46-4.46 2.57-4.53-1.4-2.05-3.59-2.33-4.36-2.36-1.86-.19-3.62 1.09-4.56 1.09-.95 0-2.4-1.07-3.94-1.04-2.03.03-3.9 1.18-4.95 3-2.1 3.64-.54 9.03 1.51 11.99 1 1.45 2.2 3.08 3.77 3.02 1.52-.06 2.09-.98 3.93-.98s2.35.98 3.95.95c1.64-.03 2.67-1.48 3.67-2.94 1.16-1.69 1.64-3.33 1.66-3.42-.04-.02-3.19-1.22-3.22-4.85zM14.4 3.91c.84-1.02 1.41-2.43 1.25-3.84-1.21.05-2.67.81-3.54 1.82-.78.9-1.46 2.34-1.28 3.71 1.35.1 2.73-.68 3.57-1.69z"/>
    </svg>
  );
}
